import { forwardRef, useCallback } from 'react'

import {
  AUDIT_DESCRIPTION_MAX_LENGTH,
  AUDIT_EXTRA_AUDITORS_MAX_LENGTH,
  AuditTypes
} from '@cdab/scania/qpr/schema'
import {
  capitalizeFirstLetter,
  enumToTranslationText,
  formatError,
  formatISODate,
  useForm
} from '@cdab/utils'
import type { TdsTextFieldCustomEvent } from '@scania/tegel'
import {
  TdsButton,
  TdsDatetime,
  TdsDropdown,
  TdsDropdownOption,
  TdsMessage,
  TdsModal,
  TdsTextField
} from '@scania/tegel-react'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { useTranslation } from 'react-i18next'
import type { AuditModalProps } from './audit-modal.types'
import { auditFormParser } from './audit-modal.validation'

export const AuditModal = observer(
  forwardRef<HTMLTdsModalElement, AuditModalProps>(function AuditModal(
    props,
    ref
  ) {
    const {
      initialDate,
      initialDescription,
      initialExtraAuditor,
      initialAuditTypeId,
      onSubmit,
      disableSave,
      header,
      errorMessage
    } = props
    const { t } = useTranslation()

    const { state, validate, setValue, values, reset } = useForm(() => ({
      parser: auditFormParser,
      initialValues: {
        date: initialDate
          ? formatISODate(new Date(initialDate)).split('T')[0]
          : formatISODate(new Date()).split('T')[0],
        description: initialDescription,
        extraAuditor: initialExtraAuditor,
        auditTypeId: initialAuditTypeId
      }
    }))

    const handleFormClose = useCallback(() => {
      if (ref && typeof ref !== 'function' && ref.current) {
        ref.current.closeModal()
      }
    }, [ref])

    const handleFormSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault()

        if (!onSubmit) {
          return
        }

        try {
          const data = await validate()

          const res = await onSubmit(data)
          if (res) {
            reset()
            handleFormClose()
          }
        } catch (e) {
          // Do nothing. Something failed
        }
      },
      [onSubmit, reset, validate, handleFormClose]
    )
    return (
      <TdsModal
        ref={ref}
        onSubmit={handleFormSubmit}
        onTdsClose={handleFormClose}
        header={header}
      >
        <div slot='body'>
          <div className='tds-u-mt3'>
            <TdsDropdown
              id='auditTypeId'
              label={capitalizeFirstLetter(t('audit-types'))}
              size='lg'
              labelPosition='outside'
              defaultValue={initialAuditTypeId?.toString()}
              onTdsChange={e => {
                const value = e.detail.value
                const auditTypeId =
                  value === null || value === 'null' ? null : parseInt(value)

                if (auditTypeId !== null) {
                  setValue('auditTypeId', auditTypeId)
                }
              }}
            >
              {(Object.keys(AuditTypes) as Array<keyof typeof AuditTypes>).map(
                (key, index) => {
                  if (parseInt(key)) {
                    return (
                      <TdsDropdownOption key={index} value={key.toString()}>
                        {t(enumToTranslationText(AuditTypes[key].toString()), {
                          ns: 'audit'
                        })}
                      </TdsDropdownOption>
                    )
                  }
                  return null
                }
              )}
            </TdsDropdown>
          </div>

          <div className='tds-u-mt3'>
            <TdsDatetime
              label={t('audit-date')}
              labelPosition='outside'
              type='date'
              value={values.date || ''}
              onTdsInput={e => setValue('date', e.target.value)}
              state={state.errors?.date ? 'error' : undefined}
              helper={formatError(state.errors?.date)}
            />
          </div>
          <div className='tds-u-mt3'>
            <TdsTextField
              labelPosition='outside'
              label={t('description')}
              placeholder={t('description')}
              maxLength={AUDIT_DESCRIPTION_MAX_LENGTH}
              value={values.description || ''}
              onTdsInput={(e: TdsTextFieldCustomEvent<InputEvent>) =>
                setValue('description', e.target.value)
              }
              state={state.errors?.description && 'error'}
              helper={formatError(state.errors?.description)}
            />
          </div>
          <div className='tds-u-mt3'>
            <TdsTextField
              labelPosition='outside'
              label={t('extra-auditor')}
              placeholder={t('extra-auditor')}
              maxLength={AUDIT_EXTRA_AUDITORS_MAX_LENGTH}
              value={values.extraAuditor || ''}
              onTdsInput={(e: TdsTextFieldCustomEvent<InputEvent>) =>
                setValue('extraAuditor', e.target.value)
              }
              helper={formatError(state.errors?.extraAuditor)}
            />
          </div>
          {errorMessage && (
            <div className='tds-u-mt3'>
              <TdsMessage
                className='tds-u-w100'
                header={errorMessage}
                variant={'error'}
              />
            </div>
          )}
        </div>
        <div slot='actions' className='tds-u-mt3 tds-u-flex tds-u-gap2'>
          <TdsButton
            disabled={disableSave}
            text={capitalizeFirstLetter(t('save'))}
            onClick={handleFormSubmit}
          />
          <TdsButton
            onClick={() => handleFormClose()}
            variant='secondary'
            text={t('cancel')}
          />
        </div>
      </TdsModal>
    )
  })
)
